import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, getDocs, orderBy } from 'firebase/firestore';
import { db } from '../../firebase/firebaseConfig';
import './DemoProjects.css';

export default function DemoProjects() {
  const navigate = useNavigate();
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [activeTab, setActiveTab] = useState('summary');
  const [demoProjects, setDemoProjects] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);

  // Fetch categories from Firestore
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const categoriesQuery = query(
          collection(db, 'labKitCategories'),
          orderBy('createdAt', 'desc')
        );
        const querySnapshot = await getDocs(categoriesQuery);
        const cats = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        console.log('Fetched categories:', cats);
        setCategories(cats);
      } catch (err) {
        console.error('Error fetching categories:', err);
      }
    };

    fetchCategories();
  }, []);

  // Fetch demo projects from Firestore
  useEffect(() => {
    const fetchDemoProjects = async () => {
      try {
        console.log('Starting to fetch demo projects from Firestore...');
        console.log('DB instance:', db);
        setLoading(true);
        // Try to fetch with orderBy first, fallback to without orderBy if it fails
        let querySnapshot;
        try {
          const projectsQuery = query(
            collection(db, 'electronicsLabKits'),
            orderBy('createdAt', 'desc')
          );
          console.log('Executing query with orderBy...');
          querySnapshot = await getDocs(projectsQuery);
          console.log('Query successful with orderBy');
        } catch (orderByError) {
          console.log('Fetching without orderBy due to:', orderByError.message);
          // If orderBy fails (missing field or index), fetch all documents
          querySnapshot = await getDocs(collection(db, 'electronicsLabKits'));
          console.log('Query successful without orderBy');
        }
        
        console.log('Total documents retrieved:', querySnapshot.docs.length);
        
        const projects = querySnapshot.docs
          .map(doc => ({
            id: doc.id,
            ...doc.data()
          }))
          .filter(project => project.active !== false) // Only show active projects
          .sort((a, b) => {
            // Sort by createdAt if available, otherwise keep original order
            if (a.createdAt && b.createdAt) {
              return b.createdAt.seconds - a.createdAt.seconds;
            }
            return 0;
          });
        
        console.log('Fetched demo projects:', projects);
        console.log('Sample project with video data:', projects.find(p => p.videoUrl || p.videoURL || p.video_url));
        setDemoProjects(projects);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching demo projects:', err);
        setError(err.message);
        setDemoProjects([]);
        setLoading(false);
      }
    };

    fetchDemoProjects();
  }, []);

  const handleProjectClick = (project) => {
    console.log('Selected project data:', project);
    console.log('Video URL:', project.videoUrl);
    console.log('All project keys:', Object.keys(project));
    setSelectedProject(project);
    setActiveTab('summary');
  };

  const handleCategoryClick = (category) => {
    setSelectedCategory(category);
  };

  const handleBackToList = () => {
    setSelectedProject(null);
  };

  const handleBackToCategories = () => {
    setSelectedCategory(null);
    setSelectedProject(null);
  };

  // Convert YouTube URL to embed format
  const getYouTubeEmbedUrl = (url) => {
    if (!url) return null;
    
    // If already in embed format, return as is
    if (url.includes('/embed/')) {
      return url;
    }
    
    let videoId = null;
    
    // Extract video ID from various YouTube URL formats
    if (url.includes('youtube.com/watch?v=')) {
      // Format: https://www.youtube.com/watch?v=VIDEO_ID
      const urlParams = new URLSearchParams(url.split('?')[1]);
      videoId = urlParams.get('v');
    } else if (url.includes('youtu.be/')) {
      // Format: https://youtu.be/VIDEO_ID
      videoId = url.split('youtu.be/')[1].split('?')[0];
    } else if (url.includes('youtube.com/embed/')) {
      // Already in embed format
      return url;
    }
    
    // If we found a video ID, return embed URL
    if (videoId) {
      return `https://www.youtube.com/embed/${videoId}`;
    }
    
    // Return original URL if we couldn't parse it
    return url;
  };

  // Get video URL from project (handles multiple possible field names)
  const getVideoUrl = (project) => {
    const possibleFields = ['videoUrl', 'videoURL', 'video_url', 'projectVideo', 'video', 'youtubeUrl', 'youtube_url'];
    
    for (const field of possibleFields) {
      if (project[field]) {
        console.log(`Found video URL in field: ${field}, value: ${project[field]}`);
        return project[field];
      }
    }
    
    console.log('No video URL found. Available fields:', Object.keys(project));
    return null;
  };

  // Copy code to clipboard
  const handleCopyCode = () => {
    if (selectedProject.codeExplanation) {
      navigator.clipboard.writeText(selectedProject.codeExplanation)
        .then(() => {
          setCopied(true);
          setTimeout(() => setCopied(false), 2000);
        })
        .catch(err => {
          console.error('Failed to copy code:', err);
        });
    }
  };

  // Download code as .ino file
  const handleDownloadCode = () => {
    if (selectedProject.codeExplanation) {
      const blob = new Blob([selectedProject.codeExplanation], { type: 'text/plain' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${selectedProject.title.replace(/\s+/g, '_')}.ino`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    }
  };

  // Display loading state
  if (loading) {
    return (
      <div className="dp-container">
        <div className="dp-hero">
          <h1>Demo Projects</h1>
          <p>Loading projects...</p>
        </div>
      </div>
    );
  }

  const renderTabContent = () => {
    if (!selectedProject) return null;

    switch (activeTab) {
      case 'summary':
        return (
          <div className="dp-tab-content">
            <h3>Project Summary</h3>
            <p>{selectedProject.summary || selectedProject.description}</p>
            {(selectedProject.category || selectedProject.estimatedTime) && (
              <div className="dp-project-meta">
                {selectedProject.category && (
                  <div className="dp-meta-item">
                    <span className="dp-meta-label">Category:</span>
                    <span className="dp-meta-value">{selectedProject.category}</span>
                  </div>
                )}
                {selectedProject.estimatedTime && (
                  <div className="dp-meta-item">
                    <span className="dp-meta-label">Estimated Time:</span>
                    <span className="dp-meta-value">{selectedProject.estimatedTime}</span>
                  </div>
                )}
              </div>
            )}
          </div>
        );

      case 'hardware':
        let hardwareList = [];
        if (selectedProject.hardwareRequired && Array.isArray(selectedProject.hardwareRequired)) {
          hardwareList = selectedProject.hardwareRequired;
        } else if (selectedProject.components) {
          if (Array.isArray(selectedProject.components)) {
            hardwareList = selectedProject.components;
          } else if (typeof selectedProject.components === 'string') {
            hardwareList = selectedProject.components.split(',').map(c => c.trim());
          }
        }
        return (
          <div className="dp-tab-content">
            <h3>Hardware Required</h3>
            {hardwareList.length > 0 ? (
              <>
                <ul className="dp-hardware-list">
                  {hardwareList.map((item, index) => (
                    <li key={index}>
                      <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <polyline points="20 6 9 17 4 12"></polyline>
                      </svg>
                      {item}
                    </li>
                  ))}
                </ul>
                <div className="dp-hardware-note">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <circle cx="12" cy="12" r="10"></circle>
                    <line x1="12" y1="16" x2="12" y2="12"></line>
                    <line x1="12" y1="8" x2="12.01" y2="8"></line>
                  </svg>
                  <p>All components are available in our store. Click on the "Shop Components" button to browse.</p>
                </div>
              </>
            ) : (
              <p style={{color: '#64748b', textAlign: 'center', padding: '40px 20px'}}>No hardware requirements specified for this project.</p>
            )}
          </div>
        );

      case 'working':
        return (
          <div className="dp-tab-content">
            <h3>How It Works</h3>
            {selectedProject.howItWorks ? (
              <div className="dp-working-content">
                {selectedProject.howItWorks.split('\n\n').map((paragraph, index) => (
                  <p key={index}>{paragraph}</p>
                ))}
              </div>
            ) : (
              <p style={{color: '#64748b', textAlign: 'center', padding: '40px 20px'}}>No detailed explanation available for this project.</p>
            )}
          </div>
        );

      case 'wiring':
        return (
          <div className="dp-tab-content">
            <h3>Wiring Diagram</h3>
            {(selectedProject.wiringDiagram || selectedProject.wiringDiagramUrl) ? (
              <div className="dp-wiring-diagram">
                <img 
                  src={selectedProject.wiringDiagram || selectedProject.wiringDiagramUrl} 
                  alt="Wiring Diagram"
                  onError={(e) => {
                    e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="800" height="600" viewBox="0 0 800 600"%3E%3Crect fill="%23f0f0f0" width="800" height="600"/%3E%3Ctext x="400" y="300" font-family="Arial" font-size="24" fill="%23666" text-anchor="middle"%3EWiring Diagram%3C/text%3E%3C/svg%3E';
                  }}
                />
                <p className="dp-diagram-note">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z"></path>
                    <line x1="12" y1="9" x2="12" y2="13"></line>
                    <line x1="12" y1="17" x2="12.01" y2="17"></line>
                  </svg>
                  <strong>Important:</strong> Double-check all connections before powering on. Follow proper polarity for sensors and power supplies.
                </p>
              </div>
            ) : (
              <p style={{color: '#64748b', textAlign: 'center', padding: '40px 20px'}}>No wiring diagram available for this project.</p>
            )}
          </div>
        );

      case 'code':
        let codeImageUrl = selectedProject.codeExplanationImage || 
                           selectedProject.codeImage || 
                           selectedProject.circuitImage || 
                           selectedProject.codeFiles || 
                           selectedProject.codeFileUrl ||
                           selectedProject.codeFile ||
                           (selectedProject.images && selectedProject.images.codeFile);
        
        // If it's an array, get the first element
        if (Array.isArray(codeImageUrl) && codeImageUrl.length > 0) {
          codeImageUrl = codeImageUrl[0];
        }
        
        // If it's an object with a url property, extract the url
        if (codeImageUrl && typeof codeImageUrl === 'object' && codeImageUrl.url) {
          codeImageUrl = codeImageUrl.url;
        }
        
        console.log('Code tab - All project fields:', Object.keys(selectedProject));
        console.log('Code tab - Looking for image in:', {
          codeExplanationImage: selectedProject.codeExplanationImage,
          codeImage: selectedProject.codeImage,
          circuitImage: selectedProject.circuitImage,
          codeFiles: selectedProject.codeFiles,
          codeFileUrl: selectedProject.codeFileUrl,
          codeFile: selectedProject.codeFile,
          images: selectedProject.images
        });
        console.log('Code tab - Final image URL:', codeImageUrl);
        
        return (
          <div className="dp-tab-content">
            <h3>Code Explanation</h3>
            {selectedProject.codeExplanation ? (
              <div className="dp-code-explanation">
                <pre className="dp-code-block">
                  <code>{selectedProject.codeExplanation}</code>
                </pre>
              <div className="dp-code-actions">
                <button className="dp-btn-copy-code" onClick={handleCopyCode}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                  </svg>
                  {copied ? 'Copied!' : 'Copy Code'}
                </button>
                <button className="dp-btn-download-code" onClick={handleDownloadCode}>
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                    <polyline points="7 10 12 15 17 10"></polyline>
                    <line x1="12" y1="15" x2="12" y2="3"></line>
                  </svg>
                  Download .ino File
                </button>
              </div>
              
              {/* Display code explanation image if available - AFTER the code */}
              {codeImageUrl && typeof codeImageUrl === 'string' && (
                <div className="dp-code-explanation-image" style={{marginTop: '25px'}}>
                  <img 
                    src={codeImageUrl} 
                    alt="Code Explanation Diagram"
                    onLoad={() => console.log('Code image loaded successfully:', codeImageUrl)}
                    onError={(e) => {
                      console.error('Failed to load code image:', codeImageUrl);
                      e.target.style.display = 'none';
                    }}
                  />
                </div>
              )}
              </div>
            ) : (
              <p style={{color: '#64748b', textAlign: 'center', padding: '40px 20px'}}>No code explanation available for this project.</p>
            )}
          </div>
        );

      case 'video':
        const videoUrl = getVideoUrl(selectedProject);
        console.log('Video tab - videoUrl:', videoUrl);
        console.log('Video tab - selectedProject:', selectedProject);
        return (
          <div className="dp-tab-content">
            <h3>Video Tutorial</h3>
            {videoUrl ? (
              <>
                <div className="dp-video-container">
                  <iframe
                    src={getYouTubeEmbedUrl(videoUrl)}
                    title={selectedProject.title}
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  ></iframe>
                </div>
                <div className="dp-video-url-link">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6"></path>
                    <polyline points="15 3 21 3 21 9"></polyline>
                    <line x1="10" y1="14" x2="21" y2="3"></line>
                  </svg>
                  <a href={videoUrl} target="_blank" rel="noopener noreferrer">
                    {videoUrl}
                  </a>
                </div>
                <div className="dp-video-description">
                  <p>Watch the complete step-by-step tutorial to build this project. The video covers assembly, wiring, programming, and troubleshooting.</p>
                </div>
              </>
            ) : (
              <p style={{color: '#64748b', textAlign: 'center', padding: '40px 20px'}}>No video tutorial available for this project.</p>
            )}
          </div>
        );

      default:
        return null;
    }
  };

  if (selectedProject) {
    return (
      <div className="dp-container">
        <div className="dp-project-detail">
          <button className="dp-go-back-btn" onClick={handleBackToList}>
            ← Back to Projects
          </button>
          
          <div className="dp-project-header">
            <h1>{selectedProject.title}</h1>
            {selectedProject.category && (
              <div className="dp-project-tags">
                <span className="dp-tag dp-category-tag">{selectedProject.category}</span>
              </div>
            )}
          </div>

          <div className="dp-project-tabs">
            <button 
              className={`dp-tab-btn ${activeTab === 'summary' ? 'active' : ''}`}
              onClick={() => setActiveTab('summary')}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"></path>
                <polyline points="14 2 14 8 20 8"></polyline>
                <line x1="16" y1="13" x2="8" y2="13"></line>
                <line x1="16" y1="17" x2="8" y2="17"></line>
                <polyline points="10 9 9 9 8 9"></polyline>
              </svg>
              Summary
            </button>
            <button 
              className={`dp-tab-btn ${activeTab === 'hardware' ? 'active' : ''}`}
              onClick={() => setActiveTab('hardware')}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="2" y="7" width="20" height="14" rx="2" ry="2"></rect>
                <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"></path>
              </svg>
              Hardware Required
            </button>
            <button 
              className={`dp-tab-btn ${activeTab === 'working' ? 'active' : ''}`}
              onClick={() => setActiveTab('working')}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10"></circle>
                <polyline points="12 6 12 12 16 14"></polyline>
              </svg>
              How It Works
            </button>
            <button 
              className={`dp-tab-btn ${activeTab === 'wiring' ? 'active' : ''}`}
              onClick={() => setActiveTab('wiring')}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="8" y1="6" x2="21" y2="6"></line>
                <line x1="8" y1="12" x2="21" y2="12"></line>
                <line x1="8" y1="18" x2="21" y2="18"></line>
                <line x1="3" y1="6" x2="3.01" y2="6"></line>
                <line x1="3" y1="12" x2="3.01" y2="12"></line>
                <line x1="3" y1="18" x2="3.01" y2="18"></line>
              </svg>
              Wiring Diagram
            </button>
            <button 
              className={`dp-tab-btn ${activeTab === 'code' ? 'active' : ''}`}
              onClick={() => setActiveTab('code')}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polyline points="16 18 22 12 16 6"></polyline>
                <polyline points="8 6 2 12 8 18"></polyline>
              </svg>
              Code Explanation
            </button>
            <button 
              className={`dp-tab-btn ${activeTab === 'video' ? 'active' : ''}`}
              onClick={() => setActiveTab('video')}
            >
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <polygon points="23 7 16 12 23 17 23 7"></polygon>
                <rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect>
              </svg>
              Video Tutorial
            </button>
          </div>

          <div className="dp-project-content">
            {renderTabContent()}
          </div>

          <div className="dp-project-actions">
            <button className="dp-btn-primary" onClick={() => navigate('/products')}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="9" cy="21" r="1"></circle>
                <circle cx="20" cy="21" r="1"></circle>
                <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6"></path>
              </svg>
              Shop Components
            </button>
          </div>
        </div>
      </div>
    );
  }

  // If a category is selected, show projects in that category
  if (selectedCategory && !selectedProject) {
    const categoryProjects = demoProjects.filter(
      project => project.categoryId === selectedCategory.id
    );

    return (
      <div className="dp-container">
        <div className="dp-hero">
          <button className="dp-back-button" onClick={handleBackToCategories}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 12H5M12 19l-7-7 7-7"/>
            </svg>
            Back to Categories
          </button>
          <h1>{selectedCategory.name}</h1>
          <p>{selectedCategory.description || 'Explore projects in this category'}</p>
        </div>

        {categoryProjects.length === 0 ? (
          <div className="dp-empty">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{width: '80px', height: '80px', color: '#94a3b8', marginBottom: '20px'}}>
              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
              <line x1="9" y1="9" x2="15" y2="9"></line>
              <line x1="9" y1="15" x2="15" y2="15"></line>
            </svg>
            <h2 style={{fontSize: '1.8rem', color: '#1E3A5F', marginBottom: '10px'}}>No Projects in This Category</h2>
            <p style={{fontSize: '1.1rem', color: '#64748b'}}>
              Projects will appear here once they are added to this category.
            </p>
          </div>
        ) : (
          <div className="dp-projects-grid" style={{maxWidth: '1400px', margin: '0 auto'}}>
            {categoryProjects.map((project) => (
              <div key={project.id} className="dp-project-card" onClick={() => handleProjectClick(project)}>
                <div className="dp-project-thumbnail">
                  <img 
                    src={project.thumbnail || project.imageUrl} 
                    alt={project.title}
                    onError={(e) => {
                      e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="300" viewBox="0 0 400 300"%3E%3Crect fill="%23f0f0f0" width="400" height="300"/%3E%3Ctext x="200" y="150" font-family="Arial" font-size="18" fill="%23666" text-anchor="middle"%3EProject Image%3C/text%3E%3C/svg%3E';
                    }}
                  />
                </div>
                <div className="dp-project-info">
                  <h3>{project.title}</h3>
                  <p>{(project.summary || project.description || '').substring(0, 120)}{(project.summary || project.description || '').length > 120 ? '...' : ''}</p>
                  <div className="dp-project-meta-footer">
                    {project.category && <span className="dp-category-badge">{project.category}</span>}
                    {project.estimatedTime && (
                      <span className="dp-time-badge">
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <circle cx="12" cy="12" r="10"></circle>
                          <polyline points="12 6 12 12 16 14"></polyline>
                        </svg>
                        {project.estimatedTime}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="dp-container">
      <div className="dp-hero">
        <button className="dp-back-button" onClick={() => navigate('/')}>
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
            <path d="M19 12H5M12 19l-7-7 7-7"/>
          </svg>
          Back to Home
        </button>
        <h1>Electronics Lab Kits</h1>
        <p>Explore hands-on electronics and IoT projects with complete tutorials, code, and wiring diagrams</p>
      </div>

      {demoProjects.length === 0 ? (
        <div className="dp-empty">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{width: '80px', height: '80px', color: '#94a3b8', marginBottom: '20px'}}>
            <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
            <line x1="9" y1="9" x2="15" y2="9"></line>
            <line x1="9" y1="15" x2="15" y2="15"></line>
          </svg>
          <h2 style={{fontSize: '1.8rem', color: '#1E3A5F', marginBottom: '10px'}}>No Projects Available</h2>
          <p style={{fontSize: '1.1rem', color: '#64748b', marginBottom: '30px'}}>
            Projects will appear here once they are added from the admin panel.
          </p>
          {error && (
            <p style={{color: '#D32F2F', fontSize: '0.95rem', marginBottom: '20px'}}>
              Error: {error}
            </p>
          )}
        </div>
      ) : (
        <div className="dp-categories-grid">
          {categories.map((category) => {
            const categoryProjects = demoProjects.filter(
              project => project.categoryId === category.id
            );
            
            if (categoryProjects.length === 0) return null;
            
            return (
              <div 
                key={category.id} 
                className="dp-category-card" 
                onClick={() => handleCategoryClick(category)}
              >
                <div className="dp-category-icon">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path>
                    <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"></path>
                  </svg>
                </div>
                <h3>{category.name}</h3>
                <p>{category.description || 'Click to explore projects'}</p>
                <div className="dp-category-count">
                  {categoryProjects.length} {categoryProjects.length === 1 ? 'Project' : 'Projects'}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
